import {
  AdminCreateOAuthClient,
  AdminFetchOAuthClient,
  AdminRevokeOAuthTokens,
  AdminUpdateOAuthClient,
} from 'src/constants/actionTypes'
import type { OAuthClient } from 'src/interfaces/oauth'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import type { Action } from 'src/utils/reduxUtils'
import type { OAuthClientState } from './types'

const getInitialState = (): OAuthClientState => {
  return {
    partnerId: null,
    client: null,
    loading: false,
    loaded: false,
    error: null,
    saving: false,
    revoking: false,
  }
}

export const oauthClientReducer = (
  state: OAuthClientState = getInitialState(),
  action: Action<unknown>,
): OAuthClientState => {
  switch (action.type) {
    case AdminFetchOAuthClient.type.REQUEST: {
      const partnerId = (action.payload as { partnerId?: string } | undefined)?.partnerId
      const isSamePartner = Boolean(partnerId) && state.partnerId === partnerId

      // The Retool app's "select row → clear oauth secrets" behaviour is satisfied by the
      // page, which drops its locally-held secret on a customerId change — the secret is not
      // in this slice, so there is nothing to clear here.
      //
      // `saving` / `revoking` are reset on a partner change because the SUCCESS cases below
      // are partner-guarded: a mutation for the previous customer that resolves after the
      // switch returns early and never clears its own in-flight flag. Without this reset the
      // new customer's page would inherit `saving: true` and keep Save and Enable/Disable
      // disabled with no way to clear them — the submit that would clear the flag is itself
      // the control that is disabled.
      return {
        ...state,
        partnerId: partnerId ?? state.partnerId,
        client: isSamePartner ? state.client : null,
        loaded: isSamePartner ? state.loaded : false,
        saving: isSamePartner ? state.saving : false,
        revoking: isSamePartner ? state.revoking : false,
        loading: true,
        error: null,
      }
    }
    case AdminFetchOAuthClient.type.SUCCESS: {
      const payload = action.payload as
        | { partnerId: string; client: OAuthClient | null }
        | undefined

      if (payload === undefined || payload.partnerId !== state.partnerId) {
        return state
      }

      return {
        ...state,
        client: payload.client,
        loading: false,
        loaded: true,
        error: null,
      }
    }
    case AdminFetchOAuthClient.type.FAILED:
      return {
        ...state,
        loading: false,
        error: resolveErrorMessage(action.payload, 'Failed to load OAuth client'),
      }

    case AdminCreateOAuthClient.type.REQUEST:
    case AdminUpdateOAuthClient.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    case AdminCreateOAuthClient.type.SUCCESS: {
      const payload = action.payload as { partnerId: string; client: OAuthClient } | undefined

      if (payload === undefined || payload.partnerId !== state.partnerId) {
        return state
      }

      return {
        ...state,
        client: payload.client,
        loaded: true,
        saving: false,
      }
    }
    case AdminUpdateOAuthClient.type.SUCCESS: {
      const payload = action.payload as { partnerId: string; client: OAuthClient } | undefined

      if (payload === undefined || payload.partnerId !== state.partnerId) {
        return state
      }

      return {
        ...state,
        client: payload.client,
        saving: false,
      }
    }
    case AdminCreateOAuthClient.type.FAILED:
    case AdminUpdateOAuthClient.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    case AdminRevokeOAuthTokens.type.REQUEST:
      return {
        ...state,
        revoking: true,
      }
    // Revoking tokens changes no field on the client itself, so `client` is left alone.
    case AdminRevokeOAuthTokens.type.SUCCESS:
    case AdminRevokeOAuthTokens.type.FAILED:
      return {
        ...state,
        revoking: false,
      }

    default:
      return state
  }
}
